import React from 'react';
import { motion } from 'framer-motion';
export interface BudgetSegment {
  label: string;
  amount: number;
  color?: string;
}
export interface BudgetBarProps {
  totalBudget: number;
  segments: BudgetSegment[];
  showRemaining?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
const SEGMENT_COLORS = [{
  bg: 'bg-blue-500',
  light: 'bg-blue-100',
  text: 'text-blue-700',
  dot: 'bg-blue-500'
}, {
  bg: 'bg-amber-500',
  light: 'bg-amber-100',
  text: 'text-amber-700',
  dot: 'bg-amber-500'
}, {
  bg: 'bg-rose-500',
  light: 'bg-rose-100',
  text: 'text-rose-700',
  dot: 'bg-rose-500'
}, {
  bg: 'bg-purple-500',
  light: 'bg-purple-100',
  text: 'text-purple-700',
  dot: 'bg-purple-500'
}, {
  bg: 'bg-teal-500',
  light: 'bg-teal-100',
  text: 'text-teal-700',
  dot: 'bg-teal-500'
}, {
  bg: 'bg-orange-500',
  light: 'bg-orange-100',
  text: 'text-orange-700',
  dot: 'bg-orange-500'
}];
export function BudgetBar({
  totalBudget,
  segments,
  showRemaining = true,
  label,
  mode = 'support'
}: BudgetBarProps) {
  if (!totalBudget || totalBudget <= 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-11735">
        <p className="text-red-600 text-sm font-medium" data-id="element-11736">
          Geen budget opgegeven
        </p>
      </div>;
  }
  const validSegments = (segments || []).filter(s => s.label && typeof s.amount === 'number' && s.amount > 0);
  const totalSpent = validSegments.reduce((sum, s) => sum + s.amount, 0);
  const remaining = totalBudget - totalSpent;
  const overBudget = remaining < 0;
  const spentPct = Math.min(totalSpent / totalBudget * 100, 100);
  // ─── Stimulus mode: compact bar ─────────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-slate-50 rounded-xl px-4 py-3.5 border border-slate-200" data-id="element-11737">
        {label && <p className="text-xs font-semibold text-slate-700 mb-2.5 text-center" data-id="element-11738">
            {label}
          </p>}

        {/* Budget header */}
        <div className="flex justify-between items-baseline mb-2" data-id="element-11739">
          <span className="text-xs text-slate-500" data-id="element-11740">Budget</span>
          <span className="text-sm font-bold text-slate-800 font-mono tabular-nums" data-id="element-11741">
            {formatEuro(totalBudget)}
          </span>
        </div>

        {/* Bar */}
        <div className="h-5 bg-slate-200 rounded-full overflow-hidden flex" data-id="element-11742">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            width: 0
          }} animate={{
            width: `${Math.min(pct, 100)}%`
          }} transition={{
            delay: i * 0.1,
            duration: 0.5,
            ease: 'easeOut'
          }} className={`${colors.bg} h-full ${i === 0 ? 'rounded-l-full' : ''}`} title={`${seg.label}: ${formatEuro(seg.amount)}`} data-id="element-11743" />;
        })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2" data-id="element-11744">
          {validSegments.map((seg, i) => {
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <div key={i} className="flex items-center gap-1" data-id="element-11745">
                <div className={`w-2 h-2 rounded-full ${colors.dot}`} data-id="element-11746" />
                <span className="text-xs text-slate-600" data-id="element-11747">{seg.label}</span>
              </div>;
        })}
          {showRemaining && remaining > 0 && <div className="flex items-center gap-1" data-id="element-11748">
              <div className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-11749" />
              <span className="text-xs text-emerald-600" data-id="element-11750">Over</span>
            </div>}
        </div>
      </div>;
  }
  // ─── Support mode: full educational panel ───────────────────────────
  return <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border-2 border-slate-200 shadow-sm overflow-hidden" data-id="element-11751">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-slate-200 bg-white/60" data-id="element-11752">
        <div className="flex items-center justify-between" data-id="element-11753">
          <div data-id="element-11754">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-11755">
              {label || '💰 Budget overzicht'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-11756">
              Totaal budget: {formatEuro(totalBudget)}
            </p>
          </div>
          <div className={`text-right px-3 py-1.5 rounded-lg ${overBudget ? 'bg-red-100' : 'bg-emerald-100'}`} data-id="element-11757">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide" data-id="element-11758">
              {overBudget ? 'Tekort' : 'Over'}
            </div>
            <div className={`text-sm font-bold font-mono tabular-nums ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-11759">
              {formatEuro(Math.abs(remaining))}
            </div>
          </div>
        </div>
      </div>

      {/* Bar visualization */}
      <div className="px-5 py-4" data-id="element-11760">
        {/* The bar */}
        <div className="h-8 bg-slate-200 rounded-full overflow-hidden flex mb-4 relative" data-id="element-11761">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            width: 0
          }} animate={{
            width: `${Math.min(pct, 100)}%`
          }} transition={{
            delay: i * 0.15,
            duration: 0.6,
            ease: 'easeOut'
          }} className={`${colors.bg} h-full flex items-center justify-center relative ${i === 0 ? 'rounded-l-full' : ''}`} data-id="element-11762">
                {pct > 12 && <span className="text-[10px] font-bold text-white/90 truncate px-1" data-id="element-11763">
                    {Math.round(pct)}%
                  </span>}
              </motion.div>;
        })}
          {/* Remaining space */}
          {remaining > 0 && <div className="flex-1 h-full flex items-center justify-center" data-id="element-11764">
              {remaining / totalBudget * 100 > 12 && <span className="text-[10px] font-semibold text-slate-400" data-id="element-11765">
                  {Math.round(remaining / totalBudget * 100)}%
                </span>}
            </div>}
        </div>

        {/* Segment breakdown */}
        <div className="space-y-2" data-id="element-11766">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1 + 0.3,
            duration: 0.3
          }} className={`flex items-center justify-between ${colors.light} rounded-lg px-3 py-2`} data-id="element-11767">
                <div className="flex items-center gap-2" data-id="element-11768">
                  <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} data-id="element-11769" />
                  <span className={`text-sm font-medium ${colors.text}`} data-id="element-11770">
                    {seg.label}
                  </span>
                </div>
                <div className="flex items-baseline gap-2" data-id="element-11771">
                  <span className="text-xs text-slate-400" data-id="element-11772">
                    {Math.round(pct)}%
                  </span>
                  <span className={`text-sm font-bold font-mono tabular-nums ${colors.text}`} data-id="element-11773">
                    {formatEuro(seg.amount)}
                  </span>
                </div>
              </motion.div>;
        })}

          {/* Remaining */}
          {showRemaining && <motion.div initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: validSegments.length * 0.1 + 0.3,
          duration: 0.3
        }} className={`flex items-center justify-between rounded-lg px-3 py-2 ${overBudget ? 'bg-red-100' : 'bg-emerald-100'}`} data-id="element-11774">
              <div className="flex items-center gap-2" data-id="element-11775">
                <div className={`w-2.5 h-2.5 rounded-full ${overBudget ? 'bg-red-500' : 'bg-emerald-500'}`} data-id="element-11776" />
                <span className={`text-sm font-medium ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-11777">
                  {overBudget ? 'Tekort' : 'Over'}
                </span>
              </div>
              <span className={`text-sm font-bold font-mono tabular-nums ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-11778">
                {formatEuro(Math.abs(remaining))}
              </span>
            </motion.div>}
        </div>
      </div>

      {/* Educational tip */}
      <div className="bg-white/70 px-5 py-3 border-t border-slate-100" data-id="element-11779">
        <div className="flex items-start gap-2.5" data-id="element-11780">
          <span className="text-slate-400 text-sm mt-0.5" data-id="element-11781">💡</span>
          <p className="text-xs text-slate-700 leading-relaxed" data-id="element-11782">
            <strong data-id="element-11783">Tip:</strong>{' '}
            {overBudget ? 'De uitgaven zijn hoger dan het budget. Kijk welke uitgave je kunt verlagen of weglaten.' : `Je hebt ${Math.round(remaining / totalBudget * 100)}% van je budget over. Dat is ${formatEuro(remaining)}.`}
          </p>
        </div>
      </div>
    </div>;
}